import { useTranslation } from 'next-i18next';
import nookies from 'nookies';
import { useEffect, useRef, useState } from 'react';
import InputDs from '@/components/common/design-system/Input.ds';
import FullScreenContainerV2 from '@/components/layout/modals/FullScreenContainerV2';
import { ModalHeaderDsProps } from '@/components/layout/modals/ModalHeader.ds';
import { HttpVirtualAssistantRepository } from '@/components/virtual-assistant/http-virtual-assistant.repository';
import { makeRequestAdvice } from '@/components/virtual-assistant/request-advice';
import { requestMapper } from '@/components/virtual-assistant/request-mapper';
import { StubVirtualAssistantRepository } from '@/components/virtual-assistant/stub-virtual-assistant.repository';
import { VirtualAssistantBeta } from '@/components/virtual-assistant/virtual-assistant-beta';
import VirtualAssistantButtons from '@/components/virtual-assistant/virtual-assistant-buttons';
import { ANIMATION } from '@/constants/animation';
import { URLS } from '@/constants/urls';
import { useAnimation } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux';
import { openRefreshModal, setToRefresh } from '@/redux/slices/assistant.slice';
import styles from './virtual-assistant-modal.module.scss';
import { AssistantMessage, VirtualAssistantTimeline } from './virtual-assistant-timeline';

type Props = {
	isOpen: boolean;
	closeModal: () => void;
	implementation?: 'stub' | 'http';
};

export const VirtualAssistantModal = ({ isOpen, closeModal, implementation = 'http' }: Props) => {
	const { animationState: animation, handleCloseModal } = useAnimation();
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const storeId = useAppSelector(({ root }) => root.storeConf.storeId);
	const toRefresh = useAppSelector(({ assistant }) => assistant.toRefresh);
	const contentId = useAppSelector(({ assistant }) => assistant.contentId);
	const pageType = useAppSelector(({ assistant }) => assistant.pageType);
	const initialQuery = useAppSelector(({ assistant }) => assistant.initialQuery);

	const refInput = useRef(null);

	const requestAdvice = useRef(
		makeRequestAdvice(
			implementation === 'http' ? new HttpVirtualAssistantRepository() : new StubVirtualAssistantRepository(),
			storeId,
		),
	).current;

	const [value, setValue] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [messages, setMessages] = useState<AssistantMessage[]>([]);

	function manageInputHeight() {
		if (!refInput) {
			return;
		}
		refInput.current.style.height = '0px';
		refInput.current.style.height = `${Math.max(48, refInput.current.scrollHeight)}px`;
	}

	const submitLogic = async (valueTrimmed: string) => {
		const botId = `bot-${Date.now()}`;

		const init = valueTrimmed === '__INIT__';

		setIsLoading(true);
		if (init) {
			setMessages((prev) => [
				...prev,
				{
					id: botId,
					role: 'assistant',
					text: '',
					status: 'pending',
				},
			]);
		} else {
			setMessages((prev) => [
				...prev,
				{
					id: `user-${Date.now()}`,
					role: 'user',
					text: valueTrimmed,
					status: 'done',
				},
				{
					id: botId,
					role: 'assistant',
					text: '',
					status: 'pending',
				},
			]);
		}

		setValue('');

		try {
			const response = await requestAdvice(valueTrimmed, init, contentId, pageType);

			if (init && response.history && response.history.length > 0) {
				const mappedResponse = requestMapper(response);
				if (response.history.length > 0) {
					setMessages(mappedResponse.history);
				}
			}

			setMessages((prev) =>
				prev.map((m) =>
					m.id === botId
						? {
								...m,
								text: response.message,
								suggestions: response.suggestions,
								products: response.baseProducts,
								status: 'done',
							}
						: m,
				),
			);
		} finally {
			setIsLoading(false);
			requestAnimationFrame(() => {
				refInput.current?.focus();
				manageInputHeight();
			});
		}
	};

	const handleChange = (value: string) => {
		setValue(value);
		manageInputHeight();
	};

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		(async () => {
			await submitLogic('__INIT__');
			if (initialQuery) {
				await submitLogic(initialQuery);
			}
		})();
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const previousBg = document.body.style.backgroundColor;
		document.body.style.backgroundColor = '#fff';
		return () => {
			document.body.style.backgroundColor = previousBg;
		};
	}, [isOpen]);

	useEffect(() => {
		if (toRefresh) {
			nookies.set(null, 'assistantSessionId', '', { expiresIn: '2d' });
			setMessages([]);
			submitLogic('__INIT__');
			dispatch(setToRefresh());
		}
	}, [toRefresh]);

	const handleShortAdvice = async (advice: string) => {
		const trimmed = advice.trim();
		if (!trimmed) {
			return;
		}
		manageInputHeight();

		return await submitLogic(trimmed);
	};

	const onClose = () => {
		handleCloseModal();
		setTimeout(closeModal, ANIMATION.DELAY_MODAL);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleShortAdvice(value);
		}
	};

	const refreshHistory = () => {
		dispatch(openRefreshModal());
	};

	const headerProps = {
		whichButton: 'bothButtons',
		iconButtonLeft: URLS.REFRESH_BLACK,
		onReturnClick: refreshHistory,
		onCloseClick: onClose,
		title: t('assistant.name'),
	} satisfies ModalHeaderDsProps;

	return (
		<FullScreenContainerV2
			padding="none"
			onClose={onClose}
			overlay
			isOpen={isOpen}
			designSystemHeader={headerProps}
			animationClass={animation}
			classNames={{
				container: styles.container,
				content: styles.content,
			}}
		>
			<VirtualAssistantTimeline messages={messages} isLoading={isLoading} submitChanges={handleShortAdvice} />
			<div className={styles.bottom}>
				<InputDs
					ref={refInput}
					variant="textarea"
					value={value}
					isDisabled={isLoading}
					isFullWidth
					label={t('assistant.ask')}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					button={<VirtualAssistantButtons value={value} onChange={handleChange} onClick={handleShortAdvice} />}
				/>
				<VirtualAssistantBeta />
			</div>
		</FullScreenContainerV2>
	);
};
